// Pure helpers for the tutor page (047 D43–D52): the head, the tabs, and the words and numbers on
// every card. No React and no Firestore: Jest loads this file.
import { ageFrom, ageLabel } from "./studentDetail";
import { payoutCycleLabel } from "./cyclePay";
import { countText, dateTimeLines } from "./kitPresentation";
import { periodRange } from "./payCycle";
import { egp } from "./queuePresentation";
import { toDate } from "./ticketPresentation";
import { earnedText, genderText, hoursText, ratingText, verifyChip } from "./tutorPresentation";
import { visibleCertificates } from "./verificationPresentation";

// The tabs (D44), in Figma's order with Ratings and Pay added. `path` is the URL segment after
// the id; "" is the Overview.
export const TUTOR_TABS = [
  { key: "overview", label: "Overview", path: "" },
  { key: "sessions", label: "Sessions", path: "sessions" },
  { key: "schedule", label: "Schedule", path: "schedule" },
  { key: "ratings", label: "Ratings", path: "ratings" },
  { key: "pay", label: "Pay", path: "pay" },
  { key: "tickets", label: "Tickets", path: "tickets" },
  { key: "notes", label: "Notes", path: "notes" },
];

// The count pill on a tab: Sessions shows the page's lesson count once it lands; Ratings shows the
// stored numberOfRatings. null draws no pill.
export const tutorTabCount = (key, tutor, sessionsTotal) => {
  if (key === "sessions") return sessionsTotal ?? null;
  if (key === "ratings") return tutor?.numberOfRatings ?? null;
  return null;
};

// The head's chips (D45): the verify status in the list's tones, then Disabled and Internal.
export const tutorChips = (tutor) => {
  const chips = [verifyChip(tutor)];
  if (tutor?.disabled) chips.push({ label: "Disabled", color: "error" });
  if (tutor?.isInternal) chips.push({ label: "Internal", color: "default" });
  return chips;
};

// The head's second line (D45): "Male · 36 years old · Joined 23 Aug 2026". A gender or an age the
// tutor never gave is left out.
export const tutorMeta = (tutor, today = new Date()) => {
  const gender = genderText(tutor?.gender);
  return [
    gender === "—" ? "" : gender,
    ageLabel(ageFrom(tutor?.date_of_birth, today)),
    `Joined ${dateTimeLines(tutor?.createdAt)?.date || "-"}`,
  ]
    .filter(Boolean)
    .join(" · ");
};

const known = (value) => typeof value === "number" && Number.isFinite(value);

// "85%": a whole-number share of the total, or "" when either number is missing or the total is 0.
export const shareText = (value, total) =>
  known(value) && known(total) && total > 0 ? `${Math.round((value * 100) / total)}%` : "";

// Performance metrics (D48; Figma 10982:85741): three rows under the donut, in its colours. The
// red row is Awaiting outcome, since no no-show is stored.
export const PERFORMANCE_ROWS = [
  { key: "finished", label: "Completed", tone: "teal" },
  { key: "cancelled", label: "Cancelled", tone: "amber" },
  { key: "awaiting", label: "Awaiting outcome", tone: "red" },
];

// `counts` holds total, finished, cancelled and awaiting. A count that has not landed, or failed,
// is null or missing: its row reads "—" and its caption has no share.
export const performanceRows = (counts = {}) =>
  PERFORMANCE_ROWS.map((row) => {
    const value = counts[row.key];
    const share = shareText(value, counts.total);
    return { ...row, value: known(value) ? countText(value) : "—", caption: share ? `${share} ${row.label}` : row.label };
  });

// The donut's arcs, clockwise from 12 o'clock in the rows' order, as fractions of the total. The rest
// of the ring (upcoming lessons, and any count still loading) stays the track grey.
export const performanceSegments = (counts = {}) =>
  known(counts.total) && counts.total > 0
    ? PERFORMANCE_ROWS.filter((row) => known(counts[row.key]) && counts[row.key] > 0).map((row) => ({
        key: row.key,
        tone: row.tone,
        fraction: counts[row.key] / counts.total,
      }))
    : [];

// Feedback rate (D49; Figma 10982:85773): completed lessons against the ones the tutor wrote a note
// to the parent on (029 D14). `rate` is 0–100, or null when it cannot be worked out.
export const feedbackStats = ({ finished, withNote } = {}) => {
  const ready = known(finished) && known(withNote);
  const rate = ready && finished > 0 ? Math.min(100, (withNote * 100) / finished) : null;
  const missing = ready ? Math.max(0, finished - withNote) : null;
  return {
    rate,
    rateText: rate === null ? "—" : `${rate.toFixed(1)}%`,
    rows: [
      { key: "finished", label: "Completed sessions", value: known(finished) ? countText(finished) : "—" },
      { key: "notes", label: "Notes written", value: known(withNote) ? countText(withNote) : "—" },
      { key: "missing", label: "Missing", value: missing === null ? "—" : countText(missing) },
    ],
  };
};

// "4.2 average from 5 reviews", or "No reviews yet".
export const reviewsLine = (tutor) => {
  const n = Number(tutor?.numberOfRatings) || 0;
  return n > 0 ? `${ratingText(tutor.rating)} average from ${countText(n)} ${n === 1 ? "review" : "reviews"}` : "No reviews yet";
};

// A certificate's years: "2015–2018", the one year there is, or "—".
export const certificateYears = (certificate = {}) => {
  const { startYear, endYear, year } = certificate;
  if (startYear && endYear && String(startYear) !== String(endYear)) return `${startYear}–${endYear}`;
  return String(year || endYear || startYear || "—");
};

// The Documents card (D50): the certificates the tutor added in the app. A file opens only when its
// address is a web address.
export const documentRows = (tutor) =>
  visibleCertificates(tutor).map((certificate, i) => ({
    key: certificate.id || String(i),
    name: certificate.name || "Untitled certificate",
    organization: certificate.organization || "",
    years: certificateYears(certificate),
    fileUrl: typeof certificate.fileUrl === "string" && /^https?:\/\//i.test(certificate.fileUrl) ? certificate.fileUrl : "",
  }));

// The Schedule tab (D51): the app keys a tutor's week by day name, Monday first.
export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const slotText = (slot) => (slot && slot.start && slot.end ? `${slot.start}–${slot.end}` : "");

// Every weekday with its hours ("17:00–20:00, 21:00–22:00"), or Off when the day is switched off or
// has no hours.
export const scheduleRows = (availability) =>
  WEEKDAYS.map((day) => {
    const entry = availability && typeof availability === "object" ? availability[day] : null;
    const slots = entry && Array.isArray(entry.slots) ? entry.slots.map(slotText).filter(Boolean) : [];
    const on = Boolean(entry && entry.enabled) && slots.length > 0;
    return { day, on, hours: on ? slots.join(", ") : "Off" };
  });

// "Times in Africa/Cairo · 5 days a week".
export const scheduleSubtitle = (tutor) => {
  const days = scheduleRows(tutor?.availability).filter((row) => row.on).length;
  const zone = tutor?.timeZone ? `Times in ${tutor.timeZone}` : "No time zone set";
  return `${zone} · ${days === 0 ? "no teaching days" : `${days} ${days === 1 ? "day" : "days"} a week`}`;
};

// "Pay cycle 26 Aug – 25 Sep"; "" until the cycles are known.
export const cycleSubtitle = (cycles) => (cycles ? `Pay cycle ${periodRange(cycles.current)}` : "");

// Everything this tutor has been paid. An undone payment paid nothing (053 R17); a receipt that did
// not lower Owed (26 Aug – 25 Sep, D5) is money 051's opening balance already counts (053 D22).
export const payoutsTotal = (payouts = []) =>
  payouts.reduce((sum, p) => sum + (p.undoneAt || p.owedMoved === false ? 0 : Number(p.amount) || 0), 0);

// Payout & compliance (D52; Figma 10982:85907). `hours` is { current, previous, previousLessons }
// and `payouts` a list; either is null until it lands (or when it failed) and reads "—".
export const payRows = ({ tutor, hours, payouts }) => [
  { key: "current", label: "Hours this cycle", value: hours ? hoursText(hours.current) : "—" },
  { key: "previous", label: "Hours last cycle", value: hours ? hoursText(hours.previous) : "—" },
  { key: "previousEarned", label: "Last cycle (E£)", value: hours ? earnedText(hours.previousLessons, tutor) : "—" },
  { key: "rate", label: "Hourly rate", value: Number(tutor?.salary) > 0 ? egp(tutor.salary) : "Not set" },
  { key: "fixed", label: "Fixed monthly pay", value: Number(tutor?.fixedMonthlyPay) > 0 ? egp(tutor.fixedMonthlyPay) : "—" },
  { key: "owed", label: "Owed now", value: egp(tutor?.unpaidEarning) },
  { key: "paid", label: "Lifetime paid", value: payouts ? egp(payoutsTotal(payouts)) : "—" },
];

// The Pay tab's last-cycle card: the drawer's Last Cycle Details. `details` is null until the
// hours land (or when they failed), and then every row reads "—", not a confident 0.
export const cycleDetailRows = (details) => {
  const value = (n) => (details ? countText(n || 0) : "—");
  return [
    { key: "l25", label: "25-minute lessons", value: value(details?.lessons25min) },
    { key: "l55", label: "55-minute lessons", value: value(details?.lessons55min) },
    { key: "cancelled", label: "Cancelled lessons", value: value(details?.cancelledCount) },
    { key: "paid", label: "Cancelled lessons paid to the tutor", value: value(details?.cancelledCountForTeacher) },
  ];
};

// A tutor's payouts, newest first; one without a date goes last.
export const sortPayouts = (payouts = []) => {
  const at = (p) => toDate(p.createdAt)?.getTime() ?? 0;
  return [...payouts].sort((a, b) => at(b) - at(a));
};

// One payout as the Pay tab's history shows it: a cycle payment with its receipt (053), or an
// older one with its reference.
export const payoutRow = (payout = {}) => ({
  when: dateTimeLines(payout.createdAt),
  cycle: payoutCycleLabel(payout),
  amount: egp(payout.amount),
  receipt: payout.receiptPath ? payout.receiptName || "Receipt" : payout.paymentReference || "—",
  hasReceipt: Boolean(payout.receiptPath),
  undone: Boolean(payout.undoneAt),
  before: payout.unpaidBefore == null ? "—" : egp(payout.unpaidBefore),
  after: payout.unpaidAfter == null ? "—" : egp(payout.unpaidAfter),
  notes: payout.notes || "—",
});
